/**
 * BonusTurnManager - Centralized Bonus Turn State Management
 *
 * Phase 4 - SOC Refactoring
 *
 * Single source of truth for all bonus turn logic (Monkey swing/consume, Virus, Penguin capture).
 * Consolidates bonus turn state that was previously scattered across GameStateManager,
 * MovementManager, and UI components.
 *
 * RESPONSIBILITIES:
 * - Grant/clear bonus turns
 * - Track active bonus turn piece
 * - Manage swing return blocks (Monkey-specific)
 * - Validate bonus turn piece moves
 * - Determine when bonus turns should auto-end (e.g., Virus with no moves)
 */

import { GameState, IPiece, IMove, Position } from '../types';
import { GameStateManager } from './GameStateManager';

export class BonusTurnManager {
  private gameStateManager: GameStateManager;

  constructor(gameStateManager: GameStateManager) {
    this.gameStateManager = gameStateManager;
  }

  /**
   * Grant a bonus turn to a specific piece
   * Sets skipTurnChange flag and activeBonusTurnPieceId
   */
  grantBonusTurn(piece: IPiece): void {
    const gameState = this.gameStateManager.getGameState();
    gameState.skipTurnChange = true;
    gameState.activeBonusTurnPieceId = piece.id;
  }

  /**
   * Clear bonus turn state and allow turn changes
   * Also clears swing return block
   */
  clearBonusTurn(): void {
    const gameState = this.gameStateManager.getGameState();
    gameState.skipTurnChange = false;
    gameState.activeBonusTurnPieceId = null;

    // Clear swing return block when bonus turn ends
    this.clearSwingReturnBlock();
  }

  /**
   * Check if there is currently an active bonus turn
   */
  hasBonusTurn(): boolean {
    const gameState = this.gameStateManager.getGameState();
    return !!(gameState.skipTurnChange && gameState.activeBonusTurnPieceId !== null);
  }

  /**
   * Get the ID of the piece with an active bonus turn
   * Returns null if no bonus turn is active
   */
  getActiveBonusTurnPieceId(): string | null {
    const gameState = this.gameStateManager.getGameState();
    return gameState.activeBonusTurnPieceId || null;
  }

  /**
   * Check if a specific piece can move during the current bonus turn
   * Only the piece with the active bonus turn can move
   */
  canPieceMoveInBonusTurn(pieceId: string): boolean {
    if (!this.hasBonusTurn()) {
      return true; // No bonus turn active, any piece can move
    }

    const activePieceId = this.getActiveBonusTurnPieceId();
    return pieceId === activePieceId;
  }

  /**
   * Determine if bonus turn should auto-end
   * Used for pieces like Virus that auto-end when they have no valid moves
   */
  shouldAutoEndBonusTurn(piece: IPiece, validMoves: IMove[]): boolean {
    // Currently only implemented for Virus
    // Virus auto-ends bonus turn if it has no moves (excluding endTurn move)
    if (piece.type === 'Virus') {
      const nonEndTurnMoves = validMoves.filter(m => m.moveType !== 'End Turn');
      return nonEndTurnMoves.length === 0;
    }

    return false;
  }

  // ==========================================
  // Swing Return Block (Monkey-Specific Logic)
  // ==========================================
  // Prevents monkey from immediately swinging back to where it came from

  /**
   * Set swing return block for a monkey
   * Prevents immediate return swing
   */
  setSwingReturnBlock(fromPosition: Position, toPosition: Position, monkeyId: string): void {
    const gameState = this.gameStateManager.getGameState();
    gameState.lastSwingFromPosition = fromPosition;
    gameState.lastSwingToPosition = toPosition;
    gameState.lastSwingMonkeyId = monkeyId;

  }

  /**
   * Clear swing return block
   * Called when bonus turn ends
   */
  clearSwingReturnBlock(): void {
    const gameState = this.gameStateManager.getGameState();
    gameState.lastSwingFromPosition = null;
    gameState.lastSwingToPosition = null;
    gameState.lastSwingMonkeyId = null;

  }

  /**
   * Check if a swing move is blocked by the return block
   * Returns true if this monkey is trying to swing back to where it just came from
   */
  isSwingBlocked(fromPosition: Position, toPosition: Position, monkeyId: string): boolean {
    const gameState = this.gameStateManager.getGameState();

    // No block if no previous swing
    if (!gameState.lastSwingFromPosition || !gameState.lastSwingToPosition || !gameState.lastSwingMonkeyId) {
      return false;
    }

    // Only block if same monkey
    if (monkeyId !== gameState.lastSwingMonkeyId) {
      return false;
    }

    // Check if trying to swing back to original position
    const isTryingToSwingBack =
      fromPosition.row === gameState.lastSwingToPosition.row &&
      fromPosition.col === gameState.lastSwingToPosition.col &&
      toPosition.row === gameState.lastSwingFromPosition.row &&
      toPosition.col === gameState.lastSwingFromPosition.col;

    if (isTryingToSwingBack) {
    }

    return isTryingToSwingBack;
  }

  /**
   * Get current swing return block info (for debugging)
   */
  getSwingReturnBlock(): { from: Position | null; to: Position | null; monkeyId: string | null } {
    const gameState = this.gameStateManager.getGameState();
    return {
      from: gameState.lastSwingFromPosition || null,
      to: gameState.lastSwingToPosition || null,
      monkeyId: gameState.lastSwingMonkeyId || null
    };
  }
}
